import { isCommittedBooking, isLinkEffectivelyExpired, isLiveBooking, linkSenderPersonId, linkSenderRole as domainLinkSender, slotBookingId } from '@atink/domain';
import { depositPaidAud } from '@/utils/bookingSummary';
import type { Booking, Deposit, LinkSenderRole, StudioId, TopupPlan } from '@/domain/types';

// ---------------------------------------------------------------------------
// Unpaid deposit links (W5 mg, 14ym9bv75mg — DECIDED 24 Sep). Pure: reads only
// what it is passed; "now" is the sim clock the caller supplies.
//
// An unpaid link routes by WHO SENT IT:
//   agent     -> an alert on the AGENT's worklist ("call back"); Ops sees it
//                READ-ONLY (past 48h, the board-08 "Links >48h" report).
//   messenger -> an OPS item, actionable (the offshore Messenger can't call).
//   sweeper   -> the Sweeper's missed-deposits list at 24h (board 08); Ops sees
//                it READ-ONLY after 48h.
// A customer-driven self-book / artist link follows up like an agent link (the
// Agent works an expired self-book link as a hot lead, board 09).
//
// "Closed" = paid, re-sent / superseded, the customer has a LATER live booking,
// Ops cancelled the held slot, or an explicit close-off (outcome + note) by the
// Sweeper or Ops. Closed links never surface anywhere.
//
// Only a GENUINE unpaid link is listed at all (W4-RFIX): nothing paid on it yet
// (board 02 — a $50+ partial is a REAL booking, its top-up chase lives on the
// plan), no top-up plan attached, and not already locked to a firm booking.
// ---------------------------------------------------------------------------

/** Unpaid past this many hours since sent -> the follow-up owner is alerted (the 24h lock). */
export const MISSED_LINK_FOLLOWUP_HOURS = 24;
/** Past this, Ops sees an agent / sweeper link (read-only) — board 08 "link >48h unclosed". */
export const MISSED_LINK_OPS_READONLY_HOURS = 48;
/** Older links drop off every list (the opsView lookback). */
export const MISSED_LINK_LOOKBACK_DAYS = 14;

/** Who follows an unpaid link up. */
export type MissedLinkOwner = 'agent' | 'ops' | 'sweeper';

export interface MissedLink {
  /** Stable row id: `missed-link-<depositId>` (the Ops `?item=` key). */
  id: string;
  depositId: string;
  customerId: string;
  studioId: StudioId;
  artistId?: string;
  amountAud: number;
  sentByRole: LinkSenderRole;
  /** The sender PERSON (sentBy, else takenByAgentId; @atink/domain linkSenderPersonId). */
  sentBy?: string;
  /** When the link was (last) sent: resentAt, else createdAt. */
  sentAt: string;
  /** Whole hours since `sentAt`. */
  ageH: number;
  owner: MissedLinkOwner;
  /** Who can act on it (always the owner; Ops acts only on its own). */
  actionableBy: MissedLinkOwner;
  /** Shows on the Ops desk (the 'unclosed-links' section). */
  opsVisible: boolean;
  /** Ops sees it but must not act (agent / sweeper-owned). */
  opsReadOnly: boolean;
  /** Expired from the link's pay-by instant (the ledger's rule), not the next sweep (B4, 7kv4). */
  linkState: 'pending' | 'expired';
  /**
   * The slot the link holds (heldBookingId ?? bookingId — the one slot key, 7w8f):
   * still held; releasing (the link has expired, the next sweep frees it);
   * released (lapsed / cancelled); or no slot.
   */
  slotState: 'held' | 'releasing' | 'released' | 'none';
  heldBookingId?: string;
  /** The appointment the link holds, when known. */
  sessionIso?: string;
  /** The link's pay-by date (deposit.expiresAt). */
  payByIso: string;
}

const HOUR = 3_600_000;
const AWAITING_CLIENT = 'awaiting-client';

function heldBookingOf(d: Deposit, bookings: Record<string, Booking>): Booking | undefined {
  const slot = slotBookingId(d);
  return slot ? bookings[slot] : undefined;
}

/**
 * Who sent the link. The recorded `sentByRole` wins; older links (sent before the
 * field existed) fall back: a gap-fill source or a confirmed-on-call hold is the
 * Sweeper's; an artist / self-book link is customer-driven; anything else is an
 * Agent's (the desk + queue link paths).
 */
export function linkSenderRole(d: Deposit, bookings: Record<string, Booking>): LinkSenderRole {
  // The ONE sender rule, shared with the ledger (@atink/domain, B4 7kv4).
  return domainLinkSender(d, bookings);
}

export function missedLinkOwner(role: LinkSenderRole): MissedLinkOwner {
  if (role === 'messenger') return 'ops';
  if (role === 'sweeper') return 'sweeper';
  return 'agent';
}

/** When the link was (last) sent. */
export function linkSentAt(d: Deposit): string {
  return d.resentAt ?? d.createdAt;
}

/** A closed link never surfaces (see the module header for the rule). */
export function isMissedLinkClosed(d: Deposit, bookings: Record<string, Booking>): boolean {
  if (d.status !== 'pending' && d.status !== 'expired') return true; // paid / forfeited / refunded / rolled
  if (d.supersededByDepositId) return true; // re-sent as a fresh link
  if (d.missedClosedAt) return true; // explicit close-off
  const heldBk = heldBookingOf(d, bookings);
  if (heldBk?.cancelReason) return true; // Ops cancelled the held slot
  const sentMs = Date.parse(linkSentAt(d));
  return Object.values(bookings).some(
    (b) =>
      b.customerId === d.customerId &&
      b.id !== d.heldBookingId &&
      b.id !== d.bookingId && // the link's own booking is not a "later" one
      isLiveBooking(b) &&
      Date.parse(b.createdAt) > sentMs,
  );
}

/**
 * A genuine unpaid LINK: no money on it yet, no top-up plan carrying it, and not
 * already the deposit of a locked (committed) booking. A part-paid deposit is a
 * real booking (board 02) and a planned one is chased on its plan, never as a
 * missed link.
 */
export function isGenuineUnpaidLink(
  d: Deposit,
  bookings: Record<string, Booking>,
  topupPlans: Record<string, TopupPlan> = {},
): boolean {
  if (depositPaidAud(d) > 0) return false;
  if (Object.values(topupPlans).some((p) => p.depositId === d.id && p.status !== 'cancelled')) return false;
  const locked = d.bookingId && d.bookingId !== d.heldBookingId ? bookings[d.bookingId] : undefined;
  return !(locked && isCommittedBooking(locked));
}

export interface BuildMissedLinksArgs {
  nowIso: string;
  deposits: Record<string, Deposit>;
  bookings: Record<string, Booking>;
  /** Top-up plans (a deposit carried by one is never a missed link). */
  topupPlans?: Record<string, TopupPlan>;
  /** Studio scope; empty / absent = every studio. */
  studioIds?: StudioId[];
}

/** Every open unpaid link past the 24h lock (within the 14-day lookback), most overdue first. */
export function buildMissedLinks(args: BuildMissedLinksArgs): MissedLink[] {
  const { nowIso, deposits, bookings, studioIds, topupPlans } = args;
  const nowMs = Date.parse(nowIso);
  const allow = studioIds && studioIds.length ? new Set(studioIds) : null;
  const rows: MissedLink[] = [];
  const seen = new Set<string>(); // one row per deposit, however the map is keyed

  for (const d of Object.values(deposits)) {
    if (seen.has(d.id)) continue;
    seen.add(d.id);
    if (allow && !allow.has(d.studioId)) continue;
    if (d.payAtStudio || d.customerId === AWAITING_CLIENT) continue; // no link was sent / no client yet
    // D02-07: a link whose overbooked slot another client paid for first isn't an
    // unpaid link; the Agent's "Date taken" alert owns that call-back.
    if (d.releasedReason === 'slot-taken') continue;
    if (!isGenuineUnpaidLink(d, bookings, topupPlans)) continue;
    if (isMissedLinkClosed(d, bookings)) continue;
    const sentAt = linkSentAt(d);
    const ageH = Math.floor((nowMs - Date.parse(sentAt)) / HOUR);
    if (ageH < MISSED_LINK_FOLLOWUP_HOURS || ageH > MISSED_LINK_LOOKBACK_DAYS * 24) continue;

    const sentByRole = linkSenderRole(d, bookings);
    const senderId = linkSenderPersonId(d);
    const owner = missedLinkOwner(sentByRole);
    const heldBk = heldBookingOf(d, bookings);
    const slotId = slotBookingId(d);
    const expired = isLinkEffectivelyExpired(d, nowIso);
    const sessionIso = heldBk?.start ?? d.discussedSlot?.startIso;
    rows.push({
      id: `missed-link-${d.id}`,
      depositId: d.id,
      customerId: d.customerId,
      studioId: d.studioId,
      ...(d.artistId ? { artistId: d.artistId } : {}),
      amountAud: d.amountAud,
      sentByRole,
      // The sender PERSON by the ledger's rule (tv-7n5f): sentBy, else takenByAgentId.
      ...(senderId ? { sentBy: senderId } : {}),
      sentAt,
      ageH,
      owner,
      actionableBy: owner,
      opsVisible: owner === 'ops' || ageH >= MISSED_LINK_OPS_READONLY_HOURS,
      opsReadOnly: owner !== 'ops',
      linkState: expired ? 'expired' : 'pending',
      slotState: !slotId ? 'none' : heldBk?.status === 'held' ? (expired ? 'releasing' : 'held') : 'released',
      ...(slotId ? { heldBookingId: slotId } : {}),
      ...(sessionIso ? { sessionIso } : {}),
      payByIso: d.expiresAt,
    });
  }
  return rows.sort((a, b) => b.ageH - a.ageH);
}

/**
 * The link + slot state in words, the same on Ops, the Ops work modal and the
 * Agent's alert (B4, 7kv4): an expired link says so from its pay-by instant.
 */
export function missedLinkStateLabel(link: Pick<MissedLink, 'linkState' | 'slotState'>): string {
  const slot =
    link.slotState === 'held'
      ? 'still held'
      : link.slotState === 'releasing'
        ? 'slot releases at next sweep'
        : link.slotState === 'released'
          ? 'slot released'
          : 'no slot held';
  if (link.linkState === 'expired') return `Expired · ${slot}`;
  return slot.charAt(0).toUpperCase() + slot.slice(1);
}
